// pages/SchoolRabbisSecondaryTopicDetail.js
import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { fetchWithAuth } from '../auth';
import Breadcrumbs from '../components/Breadcrumbs';
import { SkeletonCardGrid } from '../components/ui';

export default function SchoolRabbisSecondaryTopicDetail() {
  const { mainTopicId, id } = useParams();
  const [secondaryTopic, setSecondaryTopic] = useState(null);
  const [mainTopic, setMainTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        // Get secondary topic details (includes projects nested)
        const res = await fetchWithAuth(`/api/school-rabbis-secondary-topics/${id}/`);
        if (!res.ok) throw new Error('Failed');
        const data = await res.json();
        if (!ignore) setSecondaryTopic(data);

        // Get main topic name
        const mainRes = await fetchWithAuth(`/api/school-rabbis-main-topics/${mainTopicId}/`);
        if (mainRes.ok) {
          const mainData = await mainRes.json();
          if (!ignore) setMainTopic(mainData);
        }
      } catch {
        if (!ignore) setError('שגיאה בטעינת פרטי הנושא המשני');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [id, mainTopicId]);

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="h-6 w-64 bg-secondary-200 dark:bg-secondary-700 rounded animate-pulse mb-6" />
        <div className="h-8 w-32 bg-secondary-200 dark:bg-secondary-700 rounded animate-pulse mx-auto mb-8" />
        <SkeletonCardGrid count={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="text-center py-8">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!secondaryTopic) return null;

  const projects = (secondaryTopic.projects || []).filter(p => p.is_enabled);

  return (
    <div className="page-wrapper">
      <Breadcrumbs
        items={[
          { to: '/disciplines', label: 'תחומי דעת' },
          { to: '/school-rabbis/main-topics', label: 'רבני בתי ספר' },
          { to: `/school-rabbis/main-topics/${mainTopicId}`, label: mainTopic?.name || '...' },
          { label: secondaryTopic.name },
        ]}
      />

      <div className="toolbar-center">
        <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 dark:text-white">
          {secondaryTopic.name}
        </h2>
      </div>

      <p className="text-center mt-2 mb-6">
        <Link
          to={`/school-rabbis/main-topics/${mainTopicId}`}
          className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
        >
          חזרה לנושא הראשי
        </Link>
      </p>

      {/* Projects */}
      {projects.length > 0 && (
        <section className="mt-8">
          <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">פרויקטים</h3>
          <div className="grid-autofit">
            {projects.map(project => (
              <article
                key={project.id}
                className="group bg-white dark:bg-secondary-800 rounded-2xl shadow-card overflow-hidden transition-all duration-300 hover:shadow-card-hover hover:-translate-y-1"
              >
                <Link
                  to={`/school-rabbis/main-topics/${mainTopicId}/secondary-topics/${id}/projects/${project.id}`}
                  className="block relative overflow-hidden bg-secondary-100 dark:bg-secondary-700"
                  style={{ aspectRatio: '3/2' }}
                >
                  {project.image ? (
                    <img
                      src={project.image}
                      alt={project.name}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <svg className="w-12 h-12 text-secondary-300 dark:text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                </Link>
                <div className="p-4 md:p-5">
                  <h4 className="text-lg font-semibold text-secondary-900 dark:text-white text-center">
                    <Link
                      to={`/school-rabbis/main-topics/${mainTopicId}/secondary-topics/${id}/projects/${project.id}`}
                      className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    >
                      {project.name}
                    </Link>
                  </h4>
                  {project.resources && project.resources.length > 0 && (
                    <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1">{project.resources.length} משאבים</p>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {projects.length === 0 && (
        <p className="text-center text-secondary-500 dark:text-secondary-400 py-8">
          אין פרויקטים להצגה.
        </p>
      )}
    </div>
  );
}